import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../../../utils/axios';
import '../admin.css';
import AdminWatermark from './AdminWatermark';

const MCP_SCOPES = ['mcp:read', 'mcp:config:write', 'mcp:integrations:read'];

const SuperAdminTenantDetailPage = () => {
  const navigate = useNavigate();
  const { tenantId } = useParams();

  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('user');
  const [inviteLoading, setInviteLoading] = useState(false);

  const [passwordUserId, setPasswordUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  // Invitations state
  const [invitations, setInvitations] = useState([]);
  const [inviteCopied, setInviteCopied] = useState(null);

  // MCP tokens state
  const [mcpTokens, setMcpTokens] = useState([]);
  const [mcpLoading, setMcpLoading] = useState(false);
  const [showMcpCreate, setShowMcpCreate] = useState(false);
  const [mcpCreateName, setMcpCreateName] = useState('');
  const [mcpCreateScopes, setMcpCreateScopes] = useState(['mcp:read']);
  const [mcpCreateExpiry, setMcpCreateExpiry] = useState('');
  const [mcpCreating, setMcpCreating] = useState(false);
  const [mcpNewToken, setMcpNewToken] = useState(null);
  const [mcpCopied, setMcpCopied] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const response = await apiClient.get('/iam/me');
      const data = response.data;
      const isSuperAdmin = data.role === 'admin' && data.tenant_code === 'admin';
      if (!isSuperAdmin) {
        setError('Super admin access required');
        setTimeout(() => navigate('/dashboard'), 2500);
        return false;
      }
      setUser(data);
      return true;
    } catch (e) {
      navigate('/login');
      return false;
    }
  }, [navigate]);

  const loadTenant = useCallback(async () => {
    try {
      const res = await apiClient.get('/admin/tenants');
      const found = (res.data || []).find(t => t._id === tenantId);
      if (found) {
        setTenant(found);
      } else {
        setError('Tenant not found');
      }
    } catch (e) {
      setError('Failed to load tenant');
    }
  }, [tenantId]);

  const loadUsers = useCallback(async () => {
    if (!tenantId) return;
    try {
      const res = await apiClient.get(`/admin/tenants/${tenantId}/users`);
      setTenantUsers(res.data || []);
    } catch (e) {
      setError('Failed to load users');
    }
  }, [tenantId]);

  const loadInvitations = useCallback(async () => {
    if (!tenantId) return;
    try {
      const res = await apiClient.get(`/admin/tenants/${tenantId}/invitations`);
      setInvitations(res.data || []);
    } catch {
      // silently ignore
    }
  }, [tenantId]);

  const loadMcpTokens = useCallback(async () => {
    if (!tenantId) return;
    setMcpLoading(true);
    try {
      const res = await apiClient.get(`/admin/tenants/${tenantId}/mcp-tokens`);
      setMcpTokens(res.data || []);
    } catch {
      // silently ignore — endpoint may not be wired yet
    } finally {
      setMcpLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const ok = await checkAuth();
      if (ok) {
        await Promise.all([loadTenant(), loadUsers(), loadInvitations(), loadMcpTokens()]);
      }
      setLoading(false);
    };
    init();
  }, [checkAuth, loadTenant, loadUsers, loadInvitations, loadMcpTokens]);

  const handleInvite = async () => {
    if (!inviteEmail) { setError('Enter an email address'); return; }
    setInviteLoading(true);
    setError('');
    try {
      await apiClient.post(`/admin/tenants/${tenantId}/invite`, {
        email: inviteEmail,
        role: inviteRole,
      });
      setInviteEmail('');
      await Promise.all([loadUsers(), loadInvitations()]);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to send invite');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleDeactivate = async (u) => {
    try {
      await apiClient.put(`/admin/tenants/${tenantId}/users/${u.user_id}/status`, { is_active: !u.is_active });
      await loadUsers();
    } catch (e) {
      setError('Failed to update status');
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try {
      await apiClient.delete(`/admin/tenants/${tenantId}/users/${u.user_id}`);
      await loadUsers();
    } catch (e) {
      setError('Failed to delete user');
    }
  };

  const handleSetPassword = async (u) => {
    if (!newPassword) { setError('Enter a new password first'); return; }
    try {
      await apiClient.put(`/admin/tenants/${tenantId}/users/${u.user_id}/password`, { new_password: newPassword });
      setNewPassword('');
      setPasswordUserId(null);
      setError('✅ Password updated successfully');
      setTimeout(() => setError(''), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to set password');
    }
  };

  const handleResetPassword = async (u) => {
    try {
      const res = await apiClient.post(`/admin/tenants/${tenantId}/users/${u.user_id}/reset-password`);
      const token = res.data?.reset_token;
      if (token) {
        setError(`✅ Reset token: ${token}`);
      } else {
        setError('✅ Password reset token created');
      }
      setTimeout(() => setError(''), 10000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create reset token');
    }
  };

  const handleMcpCreate = async () => {
    if (!mcpCreateName.trim()) { setError('Token name is required'); return; }
    if (mcpCreateScopes.length === 0) { setError('Select at least one scope'); return; }
    setMcpCreating(true);
    setError('');
    try {
      const body = { name: mcpCreateName.trim(), scopes: mcpCreateScopes };
      if (mcpCreateExpiry) body.expires_in_days = parseInt(mcpCreateExpiry, 10);
      const res = await apiClient.post(`/admin/tenants/${tenantId}/mcp-tokens`, body);
      setMcpNewToken(res.data.token);
      setShowMcpCreate(false);
      setMcpCreateName('');
      setMcpCreateScopes(['mcp:read']);
      setMcpCreateExpiry('');
      await loadMcpTokens();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create token');
    } finally {
      setMcpCreating(false);
    }
  };

  const handleMcpRevoke = async (id) => {
    if (!window.confirm('Revoke this token?')) return;
    try {
      await apiClient.post(`/admin/tenants/${tenantId}/mcp-tokens/${id}/revoke`);
      await loadMcpTokens();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to revoke token');
    }
  };

  const handleMcpDelete = async (id) => {
    if (!window.confirm('Permanently delete this token record?')) return;
    try {
      await apiClient.delete(`/admin/tenants/${tenantId}/mcp-tokens/${id}`);
      await loadMcpTokens();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to delete token');
    }
  };

  const toggleMcpScope = (scope) => {
    setMcpCreateScopes(prev =>
      prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]
    );
  };

  if (loading) {
    return (
      <div className="admin-container">
        <div className="loading">Loading tenant details...</div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <>
      <AdminWatermark />
      <div className="admin-container">
        <div className="admin-header">
          <div className="header-content">
            <div className="header-left">
              <h1>{tenant?.name || 'Tenant'}</h1>
              <p>
                <span className="tenant-badge" style={{ marginRight: '0.5rem' }}>{tenant?.tenant_code}</span>
                {tenant?.domain}
              </p>
            </div>
            <div className="header-right">
              <div className="user-info">
                <span className="user-name">{user.email}</span>
                <span className="tenant-badge admin-tenant">{user.tenant_code}</span>
                <span className="role-badge">{user.role}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="admin-main">
          <span className="back-link" onClick={() => navigate('/admin/super')}>
            ← Back to Tenants
          </span>

          {error && (
            <div className="error-banner">
              <span>⚠️ {error}</span>
              <button onClick={() => setError('')}>×</button>
            </div>
          )}

          {/* Tenant Info */}
          <div className="admin-card">
            <div className="card-header">
              <h2>Tenant Details</h2>
            </div>
            <div className="status-grid">
              <div className="status-item">
                <label>Name</label>
                <span>{tenant?.name || '-'}</span>
              </div>
              <div className="status-item">
                <label>Tenant Code</label>
                <span>{tenant?.tenant_code || '-'}</span>
              </div>
              <div className="status-item">
                <label>Domain</label>
                <span>{tenant?.domain || '-'}</span>
              </div>
              <div className="status-item">
                <label>Status</label>
                <span className={`active-badge ${tenant?.is_active !== false ? 'active' : 'inactive'}`}>
                  {tenant?.is_active !== false ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="status-item">
                <label>Created</label>
                <span>{tenant?.created_at ? new Date(tenant.created_at).toLocaleString() : '-'}</span>
              </div>
            </div>
          </div>

          {/* Invite User */}
          <div className="admin-card">
            <div className="card-header">
              <h2>Invite User</h2>
            </div>
            <div className="config-grid">
              <div className="config-item">
                <label>Email</label>
                <input
                  type="email"
                  placeholder="user@example.com"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  disabled={inviteLoading}
                />
              </div>
              <div className="config-item">
                <label>Role</label>
                <select value={inviteRole} onChange={e => setInviteRole(e.target.value)} disabled={inviteLoading}>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="config-item">
                <label>&nbsp;</label>
                <button
                  className="action-btn start-btn"
                  onClick={handleInvite}
                  disabled={inviteLoading}
                  style={{ alignSelf: 'flex-start' }}
                >
                  {inviteLoading ? '⏳ Sending...' : '📧 Send Invite'}
                </button>
              </div>
            </div>
          </div>

          {/* Invitations */}
          <div className="admin-card">
            <div className="card-header">
              <h2>Invitations ({invitations.length})</h2>
              <div className="card-header-actions">
                <button className="refresh-btn" onClick={loadInvitations}>🔄 Refresh</button>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Expires</th>
                    <th>Sent</th>
                    <th>Invite Link</th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.length === 0 ? (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', color: '#a0aec0', padding: '2rem' }}>
                        No invitations sent yet.
                      </td>
                    </tr>
                  ) : (
                    invitations.map(inv => {
                      const link = `${window.location.origin}/invitation?invitation=${inv.invitation_id}`;
                      const isExpired = inv.status === 'expired' || new Date(inv.expires_at) < new Date();
                      const statusColor = inv.status === 'accepted' ? '#276749' : isExpired ? '#c53030' : '#744210';
                      const statusBg = inv.status === 'accepted' ? '#f0fff4' : isExpired ? '#fff5f5' : '#fffff0';
                      return (
                        <tr key={inv.invitation_id}>
                          <td style={{ fontWeight: 500 }}>{inv.email}</td>
                          <td>
                            <span className="role-badge" style={{ fontSize: '0.75rem' }}>{inv.role}</span>
                          </td>
                          <td>
                            <span style={{
                              display: 'inline-block',
                              padding: '0.2rem 0.6rem',
                              borderRadius: '999px',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              background: statusBg,
                              color: statusColor,
                              border: `1px solid ${statusColor}33`,
                            }}>
                              {isExpired && inv.status !== 'accepted' ? 'expired' : inv.status}
                            </span>
                          </td>
                          <td style={{ fontSize: '0.82rem', color: isExpired ? '#c53030' : undefined }}>
                            {new Date(inv.expires_at).toLocaleString()}
                          </td>
                          <td style={{ fontSize: '0.82rem' }}>
                            {new Date(inv.created_at).toLocaleString()}
                          </td>
                          <td>
                            {inv.status !== 'accepted' ? (
                              <button
                                className="action-btn"
                                style={{ minWidth: 'auto', padding: '0.3rem 0.7rem', fontSize: '0.78rem' }}
                                onClick={() => {
                                  navigator.clipboard.writeText(link).then(() => {
                                    setInviteCopied(inv.invitation_id);
                                    setTimeout(() => setInviteCopied(null), 2000);
                                  });
                                }}
                              >
                                {inviteCopied === inv.invitation_id ? '✓ Copied' : '🔗 Copy Link'}
                              </button>
                            ) : (
                              <span style={{ color: '#a0aec0', fontSize: '0.78rem' }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Users Table */}
          <div className="admin-card">
            <div className="card-header">
              <h2>Users ({tenantUsers.length})</h2>
              <div className="card-header-actions">
                <button className="refresh-btn" onClick={loadUsers}>🔄 Refresh</button>
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Last Login</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tenantUsers.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', color: '#a0aec0', padding: '2rem' }}>
                        No users found. Send an invite to add the first user.
                      </td>
                    </tr>
                  ) : (
                    tenantUsers.map(u => (
                      <React.Fragment key={u.user_id}>
                        <tr>
                          <td style={{ fontWeight: 500 }}>{u.email}</td>
                          <td>{[u.first_name, u.last_name].filter(Boolean).join(' ') || '-'}</td>
                          <td>
                            <span className="role-badge" style={{ fontSize: '0.75rem' }}>
                              {u.role || 'user'}
                            </span>
                          </td>
                          <td>
                            <span className={`active-badge ${u.is_active ? 'active' : 'inactive'}`}>
                              {u.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>{u.last_login ? new Date(u.last_login).toLocaleString() : '-'}</td>
                          <td>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                              <button
                                className="action-btn"
                                style={{ minWidth: 'auto', padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                                onClick={() => handleDeactivate(u)}
                              >
                                {u.is_active ? 'Deactivate' : 'Activate'}
                              </button>
                              <button
                                className="action-btn"
                                style={{ minWidth: 'auto', padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                                onClick={() => {
                                  setPasswordUserId(passwordUserId === u.user_id ? null : u.user_id);
                                  setNewPassword('');
                                }}
                              >
                                Set Password
                              </button>
                              <button
                                className="action-btn"
                                style={{ minWidth: 'auto', padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                                onClick={() => handleResetPassword(u)}
                              >
                                Reset Password
                              </button>
                              <button
                                className="action-btn stop-btn"
                                style={{ minWidth: 'auto', padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                                onClick={() => handleDelete(u)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                        {passwordUserId === u.user_id && (
                          <tr>
                            <td colSpan="7" style={{ background: '#f7fafc', padding: '0.75rem 1rem' }}>
                              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                <span style={{ fontSize: '0.85rem', color: '#4a5568', whiteSpace: 'nowrap' }}>
                                  New password for <strong>{u.email}</strong>:
                                </span>
                                <input
                                  type="password"
                                  value={newPassword}
                                  onChange={e => setNewPassword(e.target.value)}
                                  placeholder="Min 8 chars, upper+lower+digit+special"
                                  style={{
                                    flex: 1,
                                    padding: '0.5rem 0.75rem',
                                    border: '1px solid #cbd5e0',
                                    borderRadius: '6px',
                                    fontSize: '0.85rem'
                                  }}
                                  onKeyDown={e => { if (e.key === 'Enter') handleSetPassword(u); }}
                                />
                                <button
                                  className="action-btn start-btn"
                                  style={{ minWidth: 'auto', padding: '0.5rem 1rem', fontSize: '0.85rem' }}
                                  onClick={() => handleSetPassword(u)}
                                >
                                  Save
                                </button>
                                <button
                                  className="action-btn"
                                  style={{ minWidth: 'auto', padding: '0.5rem 1rem', fontSize: '0.85rem' }}
                                  onClick={() => { setPasswordUserId(null); setNewPassword(''); }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {/* MCP Tokens */}
          <div className="admin-card">
            <div className="card-header">
              <h2>MCP API Tokens ({mcpTokens.length})</h2>
              <div className="card-header-actions">
                <button className="refresh-btn" onClick={loadMcpTokens}>🔄 Refresh</button>
                <button className="action-btn start-btn" style={{ marginLeft: '0.5rem' }} onClick={() => setShowMcpCreate(v => !v)}>
                  {showMcpCreate ? '✕ Cancel' : '+ New Token'}
                </button>
              </div>
            </div>

            {mcpNewToken && (
              <div style={{
                background: '#f0fff4',
                border: '1px solid #9ae6b4',
                borderRadius: '6px',
                padding: '0.75rem 1rem',
                marginBottom: '1rem',
              }}>
                <div style={{ fontWeight: 600, color: '#276749', marginBottom: '0.4rem', fontSize: '0.875rem' }}>
                  ✅ Token created — copy it now, it won't be shown again
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <code style={{
                    flex: 1, padding: '0.4rem 0.6rem', background: '#fff',
                    border: '1px solid #9ae6b4', borderRadius: '4px',
                    fontSize: '0.78rem', wordBreak: 'break-all', color: '#22543d',
                  }}>{mcpNewToken}</code>
                  <button
                    className="action-btn start-btn"
                    style={{ minWidth: 'auto', padding: '0.4rem 0.9rem', fontSize: '0.8rem' }}
                    onClick={() => {
                      navigator.clipboard.writeText(mcpNewToken).then(() => {
                        setMcpCopied(true);
                        setTimeout(() => setMcpCopied(false), 2000);
                      });
                    }}
                  >
                    {mcpCopied ? '✓ Copied' : 'Copy'}
                  </button>
                  <button
                    className="action-btn"
                    style={{ minWidth: 'auto', padding: '0.4rem 0.9rem', fontSize: '0.8rem' }}
                    onClick={() => setMcpNewToken(null)}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}

            {showMcpCreate && (
              <div style={{
                background: '#f7fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                padding: '1rem',
                marginBottom: '1rem',
              }}>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ fontSize: '0.8rem', color: '#718096', display: 'block', marginBottom: '0.25rem' }}>Name *</label>
                  <input
                    type="text"
                    placeholder="e.g. cursor-agent"
                    value={mcpCreateName}
                    onChange={e => setMcpCreateName(e.target.value)}
                    disabled={mcpCreating}
                    style={{ padding: '0.45rem 0.75rem', border: '1px solid #cbd5e0', borderRadius: '6px', fontSize: '0.875rem', width: '280px' }}
                  />
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ fontSize: '0.8rem', color: '#718096', display: 'block', marginBottom: '0.25rem' }}>Scopes *</label>
                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    {MCP_SCOPES.map(s => (
                      <label key={s} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer', fontSize: '0.85rem' }}>
                        <input type="checkbox" checked={mcpCreateScopes.includes(s)} onChange={() => toggleMcpScope(s)} disabled={mcpCreating} />
                        <code style={{ fontSize: '0.78rem' }}>{s}</code>
                      </label>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ fontSize: '0.8rem', color: '#718096', display: 'block', marginBottom: '0.25rem' }}>Expires in days (blank = 365)</label>
                  <input
                    type="number" min="1" placeholder="365"
                    value={mcpCreateExpiry}
                    onChange={e => setMcpCreateExpiry(e.target.value)}
                    disabled={mcpCreating}
                    style={{ padding: '0.45rem 0.75rem', border: '1px solid #cbd5e0', borderRadius: '6px', fontSize: '0.875rem', width: '120px' }}
                  />
                </div>
                <button className="action-btn start-btn" onClick={handleMcpCreate} disabled={mcpCreating}>
                  {mcpCreating ? '⏳ Creating...' : 'Create Token'}
                </button>
              </div>
            )}

            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Prefix</th>
                    <th>Scopes</th>
                    <th>Created</th>
                    <th>Expires</th>
                    <th>Last Used</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {mcpLoading ? (
                    <tr><td colSpan="7" style={{ textAlign: 'center', color: '#a0aec0', padding: '1.5rem' }}>Loading...</td></tr>
                  ) : mcpTokens.length === 0 ? (
                    <tr><td colSpan="7" style={{ textAlign: 'center', color: '#a0aec0', padding: '1.5rem' }}>No MCP tokens for this tenant.</td></tr>
                  ) : (
                    mcpTokens.map(t => (
                      <tr key={t.id}>
                        <td style={{ fontWeight: 500 }}>{t.name}</td>
                        <td><code style={{ fontSize: '0.78rem' }}>{t.token_prefix}…</code></td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                            {(t.scopes || []).map(s => (
                              <span key={s} style={{ fontSize: '0.7rem', padding: '0.1rem 0.4rem', borderRadius: '10px', background: '#ebf4ff', color: '#2b6cb0' }}>{s}</span>
                            ))}
                          </div>
                        </td>
                        <td>{t.created_at ? new Date(t.created_at).toLocaleDateString() : '-'}</td>
                        <td>{t.expires_at ? new Date(t.expires_at).toLocaleDateString() : '∞'}</td>
                        <td>{t.last_used_at ? new Date(t.last_used_at).toLocaleString() : 'Never'}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.4rem' }}>
                            <button className="action-btn" style={{ minWidth: 'auto', padding: '0.35rem 0.7rem', fontSize: '0.8rem' }} onClick={() => handleMcpRevoke(t.id)}>
                              Revoke
                            </button>
                            <button className="action-btn stop-btn" style={{ minWidth: 'auto', padding: '0.35rem 0.7rem', fontSize: '0.8rem' }} onClick={() => handleMcpDelete(t.id)}>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SuperAdminTenantDetailPage;
